import { Link } from "react-router-dom";
import { ArrowRight, Music, Sparkles, Library, Headphones, PlayCircle } from "lucide-react";
import { BOOKS, SONG, BUNDLE } from "@/lib/catalog";
import BookCard from "@/components/BookCard";
import NewReleaseBadge from "@/components/NewReleaseBadge";
import PayPalCheckout from "@/components/PayPalCheckout";
import GiftEReaderButton from "@/components/GiftEReaderButton";
import GiftSeasonBanner from "@/components/GiftSeasonBanner";
import { useEffect, useState } from "react";
import { API, getReviews } from "@/lib/api";

const featured = BOOKS.find((b) => b.newRelease) || BOOKS[0];

function useRatings() {
  const [ratings, setRatings] = useState({});
  useEffect(() => {
    getReviews()
      .then((rows) => {
        const acc = {};
        rows.forEach((r) => {
          const a = (acc[r.product_id] ||= { sum: 0, count: 0 });
          a.sum += r.rating;
          a.count += 1;
        });
        Object.values(acc).forEach((a) => (a.avg = a.sum / a.count));
        setRatings(acc);
      })
      .catch(() => {});
  }, []);
  return ratings;
}

export default function Home() {
  const ratings = useRatings();
  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-screen flex items-center pt-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={featured.cover}
            alt=""
            className="w-full h-full object-cover opacity-30 scale-110 blur-[2px]"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B0C10] via-[#0B0C10]/80 to-[#0B0C10]/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B0C10] via-transparent to-[#0B0C10]/60" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 grid lg:grid-cols-12 gap-12 items-center w-full">
          <div className="lg:col-span-7 fade-up">
            <span className="font-mono-ui text-xs uppercase tracking-[0.3em] text-primary-gold border border-primary-gold/30 px-4 py-1.5">
              Worlds by Michael Leckie
            </span>
            <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tight leading-[0.95] mt-8">
              Stories that bend
              <br />
              <span className="text-primary-gold">time, fate & survival.</span>
            </h1>
            <p className="text-soft text-lg leading-relaxed mt-8 max-w-xl">
              From a post-fallout wasteland to the red heart of the outback and a hidden war across time
              with Elvis himself — explore
              the collected novels and original music of Australian author Michael Leckie.
            </p>
            <div className="flex flex-wrap gap-4 mt-10">
              <Link
                to="/books/suspicious-times"
                data-testid="hero-browse-books"
                className="btn-gold px-8 py-4 flex items-center gap-2 font-mono-ui text-xs uppercase tracking-[0.18em]"
              >
                Browse the Books <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/books/pixels-and-glass"
                data-testid="hero-the-song"
                className="btn-ghost px-8 py-4 flex items-center gap-2 font-mono-ui text-xs uppercase tracking-[0.18em]"
              >
                <Music className="w-4 h-4" /> Pixels and Glass
              </Link>
              <Link
                to="/reader"
                data-testid="hero-ereader"
                className="btn-ghost !border-primary-gold/60 !text-primary-gold hover:!bg-primary-gold/10 px-8 py-4 flex items-center gap-2 font-mono-ui text-xs uppercase tracking-[0.18em]"
              >
                <Headphones className="w-4 h-4" /> E-Reader · Sign in
              </Link>
              <Link
                to="/listen"
                data-testid="hero-free-sample"
                className="btn-ghost px-8 py-4 flex items-center gap-2 font-mono-ui text-xs uppercase tracking-[0.18em]"
              >
                <PlayCircle className="w-4 h-4" /> Hear a free sample
              </Link>
              <GiftEReaderButton compact testId="hero-gift-ereader" />
            </div>
            {featured.newRelease && (
              <Link
                to={`/books/${featured.id}`}
                data-testid="hero-new-release-link"
                className="group inline-flex items-center gap-3 mt-10 border-l-2 border-primary-gold pl-4 py-1"
              >
                <span className="font-mono-ui text-[10px] uppercase tracking-[0.25em] text-primary-gold">
                  Just Released
                </span>
                <span className="font-serif-display text-xl text-text-primary group-hover:text-primary-gold transition-colors">
                  {featured.title} · ${featured.price.toFixed(2)}
                </span>
                <ArrowRight className="w-4 h-4 text-primary-gold transition-transform group-hover:translate-x-1" />
              </Link>
            )}
          </div>

          <div className="lg:col-span-5 hidden lg:flex justify-center">
            <Link to={`/books/${featured.id}`} data-testid="hero-featured-cover" className="floaty relative block">
              {featured.newRelease && <NewReleaseBadge large />}
              <img
                src={featured.cover}
                alt={featured.fullTitle}
                className="w-[320px] xl:w-[380px] shadow-[0_40px_100px_rgba(0,0,0,0.8)] border border-white/10"
              />
            </Link>
          </div>
        </div>
      </section>

      {/* CATALOG */}
      <GiftSeasonBanner />
      <section id="catalog" className="max-w-7xl mx-auto px-6 sm:px-8 py-24">
        <div className="flex items-end justify-between mb-14 flex-wrap gap-4">
          <div>
            <span className="font-mono-ui text-xs uppercase tracking-[0.3em] text-primary-gold">
              The Collection
            </span>
            <h2 className="font-serif-display text-4xl sm:text-5xl font-medium mt-3">
              Every novel. One library.
            </h2>
          </div>
          <p className="text-soft text-sm max-w-xs">
            Instant digital download after secure checkout. $8.55 per title.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {BOOKS.map((b, i) => (
            <BookCard key={b.id} product={b} index={i} rating={ratings[b.id]} />
          ))}
        </div>

        {/* BUNDLE CTA */}
        <div
          data-testid="bundle-banner"
          className="mt-12 border border-primary-gold/30 bg-gradient-to-r from-[#15130c] to-[#121212] p-8 sm:p-12 flex flex-col md:flex-row md:items-center justify-between gap-8"
        >
          <div className="flex items-start gap-5">
            <div className="hidden sm:flex items-center justify-center w-14 h-14 border border-primary-gold/40 shrink-0">
              <Library className="w-6 h-6 text-primary-gold" />
            </div>
            <div>
              <span className="font-mono-ui text-xs uppercase tracking-[0.3em] text-primary-gold">
                Best Value
              </span>
              <h3 className="font-serif-display text-3xl sm:text-4xl mt-2">
                The Complete Collection
              </h3>
              <p className="text-soft text-sm mt-3 max-w-md">
                All four novels in one download. Save versus buying each title separately.
              </p>
            </div>
          </div>
          <div className="flex flex-col items-start md:items-end gap-3">
            <div className="flex items-baseline gap-3">
              <span className="text-soft text-sm line-through font-mono-ui">$34.20</span>
              <span className="font-serif-display text-4xl text-primary-gold">$28.50</span>
            </div>
            <div className="w-full md:w-72" data-testid="bundle-buy-paypal">
              <PayPalCheckout product={BUNDLE} />
            </div>
          </div>
        </div>
      </section>

      {/* SONG FEATURE */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 py-16">
        <div className="grid md:grid-cols-2 gap-12 items-center border border-white/10 bg-[#121212] p-8 sm:p-12">
          <div className="relative">
            <img
              src={SONG.cover}
              alt={SONG.title}
              className="w-full max-w-md mx-auto border border-white/10"
            />
          </div>
          <div>
            <span className="font-mono-ui text-xs uppercase tracking-[0.3em] text-[#2ec5d6] flex items-center gap-2">
              <Sparkles className="w-4 h-4" /> Original Single · Out Now
            </span>
            <h2 className="font-serif-display text-4xl sm:text-5xl font-medium mt-4">
              Pixels and Glass
            </h2>
            <p className="text-soft text-lg leading-relaxed mt-5">{SONG.hook}</p>
            <p className="text-soft text-sm mt-4">{SONG.blurb}</p>

            <div className="mt-6" data-testid="song-preview">
              <p className="font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#2ec5d6] mb-2">
                30-Second Preview
              </p>
              <audio
                controls
                controlsList="nodownload"
                preload="none"
                src={`${API}/preview/pixels-and-glass`}
                data-testid="song-preview-audio"
                className="w-full max-w-sm"
              >
                Your browser does not support audio playback.
              </audio>
            </div>

            <div className="flex flex-wrap gap-4 mt-8">
              <Link
                to="/books/pixels-and-glass"
                data-testid="song-feature-buy"
                className="btn-gold px-8 py-4 font-mono-ui text-xs uppercase tracking-[0.18em]"
              >
                Get the Track · ${SONG.price.toFixed(2)}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* AUTHOR TEASER */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 py-24">
        <div className="border-t border-white/10 pt-16 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8">
            <span className="font-mono-ui text-xs uppercase tracking-[0.3em] text-primary-gold">
              About the Author
            </span>
            <h2 className="font-serif-display text-4xl sm:text-5xl font-medium mt-4 max-w-2xl">
              Michael Leckie — soldier, storyteller, South Australian.
            </h2>
            <p className="text-soft text-lg leading-relaxed mt-6 max-w-2xl">
              A former Australian Army officer from Adelaide with a 30-year military career, Michael
              channels a lifetime of experience into ambitious, genre-spanning fiction.
            </p>
            <Link
              to="/about"
              data-testid="author-teaser-link"
              className="inline-flex items-center gap-2 mt-8 font-mono-ui text-xs uppercase tracking-[0.18em] text-primary-gold hover:gap-4 transition-all"
            >
              Read his story <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
